import { addDays, format, isEqual, parse, parseISO, startOfMonth, startOfWeek } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { twJoin } from "tailwind-merge";

import type { AssetBookingDto, BookableAssetDetailDto, BookableAssetStatisticsDto, BookableSlotDto } from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { User } from "@/components/User/User";
import { Button } from "@/design-system/Button";
import { SectionCard } from "@/design-system/SectionCard";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { dayOfWeekIndex } from "@/helpers/date";
import { canManageAnyBookableAsset } from "@/helpers/permissions";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { BookingListModal } from "@/modules/bookings/components/BookingListModal";
import { CalendarMonthView } from "@/modules/bookings/components/CalendarMonthView";
import { CalendarWeekView } from "@/modules/bookings/components/CalendarWeekView";
import { daysOptions } from "@/modules/bookings/constants";
import { getDateFromTimeString, isTimeEndOfDay } from "@/modules/bookings/helpers";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { ExportBookingsModal } from "../list-assets/components/ExportBookingsModal";

export interface AssetDetailContentProps {
  assetDetails: BookableAssetDetailDto;
  isLoadingBookings: boolean;
  bookings: AssetBookingDto[];
  isLoadingAssetStatistics: boolean;
  assetStatistics: BookableAssetStatisticsDto | undefined;
  startDate: Date;
  endDate: Date;
  view: "week" | "month";
  onChangeView: (view: "week" | "month", startDate: Date) => void;
  exportBookings: (includeHistory: boolean) => void;
  isExporting: boolean;
}
export function AssetDetailContent({
  assetDetails,
  isLoadingBookings,
  bookings,
  isLoadingAssetStatistics,
  assetStatistics,
  startDate,
  endDate,
  view,
  onChangeView,
  exportBookings,
  isExporting,
}: AssetDetailContentProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const projectId = useProjectId();
  const slug = useSlug();
  const { t } = useTranslation();
  const hasPermission = usePermission();
  const [isExportModalOpened, exportModalOpenHandler] = useBool(false);
  const [activeBookings, setActiveBookings] = useState<AssetBookingDto[]>([]);
  const getLastContinuousBooking = useCallback(
    (bookings: AssetBookingDto[], curr: AssetBookingDto): AssetBookingDto => {
      const helperDate = new Date();
      const nextBooking = bookings.find((booking) =>
        isEqual(parse(booking.startTime, "HH:mm:ss", helperDate), parse(curr.endTime, "HH:mm:ss", helperDate)),
      );
      if (nextBooking) {
        // oxlint-disable-next-line react-hooks/immutability
        return getLastContinuousBooking(bookings, nextBooking);
      }

      return curr;
    },
    [],
  );
  const assetStatus: {
    status: BookableSlotDto["state"];
    until?: Date;
  } = useMemo(() => {
    const currDate = clockDate;
    const weekday = daysOptions[dayOfWeekIndex(currDate)];
    const currBookableDay = assetDetails.bookableDays.find((day) => day.day === weekday)!;
    if (!currBookableDay.enabled || !currBookableDay.times || currBookableDay.times.length === 0) {
      return {
        status: "unavailable",
      };
    }

    // The bookable time current timestamp is in
    const currBookableTime = currBookableDay.times.find(
      (time) =>
        getDateFromTimeString(time.startTime) <= currDate &&
        getDateFromTimeString(time.endTime, isTimeEndOfDay(time.endTime) ? addDays(currDate, 1) : currDate) >= currDate,
    );
    const isNotPublished = assetDetails.publishAt && parseISO(assetDetails.publishAt) > currDate;
    const isNotAvailable = assetDetails.availableFrom && parseISO(assetDetails.availableFrom) > currDate;
    const isUnpublished = assetDetails.unpublishAt && parseISO(assetDetails.unpublishAt) < currDate;
    const isWithinBookableTime = !!currBookableTime;
    if (isNotPublished || isNotAvailable || isUnpublished || !isWithinBookableTime) {
      return {
        status: "unavailable",
      };
    }
    const todayBookings = bookings.filter((booking) => booking.date === format(currDate, "yyyy-MM-dd"));
    const presentBooking = todayBookings.find(
      (booking) =>
        getDateFromTimeString(booking.startTime) <= currDate && getDateFromTimeString(booking.endTime) >= currDate,
    );
    if (presentBooking) {
      const lastContinuousBooking = getLastContinuousBooking(todayBookings, presentBooking);

      return {
        status: "booked",
        until: getDateFromTimeString(lastContinuousBooking.endTime),
      };
    }
    const nextBooking = todayBookings.find((booking) => getDateFromTimeString(booking.startTime) > currDate);
    // If currDate is within a bookable time and there's no booking before the bookable time ends
    if (currBookableTime && (!nextBooking || nextBooking.startTime > currBookableTime.endTime)) {
      return {
        status: "available",
        until: getDateFromTimeString(currBookableTime.endTime),
      };
    }

    return {
      status: "available",
      until: nextBooking ? getDateFromTimeString(nextBooking.startTime) : undefined,
    };
  }, [clockDate, assetDetails, bookings, getLastContinuousBooking]);

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={assetDetails.name}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.bookings.list-assets.title"),
              to: routes.bookings.list({
                slug,
              }),
            },
            {
              name: assetDetails.name,
            },
          ]}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [projectId] }}
            context={{ scope: "project", entity: "bookable-asset", page: "detail", entityId: assetDetails.id }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.bookable-asset-detail.suggestion.1"),
              t("component.ask-nobi-chat-drawer.bookable-asset-detail.suggestion.2"),
            ]}
          />
          {hasPermission(canManageAnyBookableAsset) && (
            <Button
              icon={<Download01Icon />}
              variant="soft"
              onClick={exportModalOpenHandler.setTrue}
              isLoading={isExporting}
            >
              {t("page.bookings.asset-detail.actions.export")}
            </Button>
          )}
          <Button
            type="link"
            href={routes.bookings.bookAsset({
              slug,
              aid: assetDetails.id,
            })}
            variant="filled"
          >
            {t("page.bookings.asset-detail.actions.book")}
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <PageGrid.Row>
          <PageGrid.Column size="33%">
            <AssetAvailabilityCard status={assetStatus.status} until={assetStatus.until} />
          </PageGrid.Column>
          <PageGrid.Column size="33%">
            <AssetLastRenterCard
              booking={assetStatistics?.mostRecentBooking}
              isLoading={isLoadingAssetStatistics}
              onViewDetails={(booking) => {
                setActiveBookings([booking]);
              }}
            />
          </PageGrid.Column>
        </PageGrid.Row>
        <SectionCard title={t("page.bookings.asset-detail.calendar.title")}>
          {view === "week" ? (
            <CalendarWeekView
              assetDetails={assetDetails}
              bookings={bookings}
              startOfWeek={startDate}
              endOfWeek={endDate}
              onChangeWeek={(date) => onChangeView("week", date)}
              onChangeView={() => onChangeView("month", startOfMonth(new Date()))}
              isLoadingBookings={isLoadingBookings}
              onSelectBookings={setActiveBookings}
            />
          ) : (
            <CalendarMonthView
              assetDetails={assetDetails}
              bookings={bookings}
              endDate={endDate}
              startDate={startDate}
              onChangeMonth={(date) => onChangeView("month", date)}
              onChangeView={(date) =>
                onChangeView(
                  "week",
                  startOfWeek(date || new Date(), {
                    weekStartsOn: 1,
                  }),
                )
              }
              onSelectBookings={setActiveBookings}
            />
          )}
        </SectionCard>
      </Stack>
      <BookingListModal
        isOpened={activeBookings.length > 0}
        onOpenChange={(state) => !state && setActiveBookings([])}
        bookings={activeBookings}
      />
      <ExportBookingsModal
        onExport={exportBookings}
        isOpened={isExportModalOpened}
        onOpenChange={exportModalOpenHandler.set}
      />
    </DocumentPage>
  );
}

function AssetAvailabilityCard({ status, until }: { status: BookableSlotDto["state"]; until?: Date }) {
  const { t } = useTranslation();

  let statusClassName = "bg-background-success-secondary text-text-on-success-secondary";
  let statusLabel = until
    ? t("page.bookings.asset-detail.insights.status.option.available-until", {
        time: format(until, "HH:mm"),
      })
    : t("page.bookings.asset-detail.insights.status.option.available-all-day");
  if (status === "unavailable") {
    statusClassName = "bg-background-secondary text-text-secondary";
    statusLabel = t("page.bookings.asset-detail.insights.status.option.unavailable");
  } else if (status === "booked") {
    statusClassName = "bg-background-danger-secondary text-text-on-danger-secondary";
    statusLabel = until
      ? t("page.bookings.asset-detail.insights.status.option.occupied", { time: format(until, "HH:mm") })
      : t("page.bookings.asset-detail.insights.status.option.occupied", { time: "" });
  }

  return (
    <SectionCard
      label={t("page.bookings.asset-detail.insights.status.title")}
      info={t("page.bookings.asset-detail.insights.status.tooltip")}
    >
      <span
        className={twJoin("flex w-full justify-center rounded-full p-space-sm text-caption-emphasis", statusClassName)}
      >
        {statusLabel}
      </span>
    </SectionCard>
  );
}
function AssetLastRenterCard({
  booking,
  isLoading,
  onViewDetails,
}: {
  booking?: AssetBookingDto;
  isLoading: boolean;
  onViewDetails: (booking: AssetBookingDto) => void;
}) {
  const { t } = useTranslation();

  return (
    <SectionCard
      label={t("page.bookings.asset-detail.insights.last-renter.title")}
      info={t("page.bookings.asset-detail.insights.last-renter.tooltip")}
    >
      {isLoading && <Spinner size="xs" />}
      {!isLoading && booking?.author && (
        <User onClick={() => onViewDetails(booking)} shouldNavigate={false} user={booking.author} />
      )}
      {!isLoading && !booking && (
        <span className="text-caption-emphasis">
          {t("page.bookings.asset-detail.insights.last-renter.not-available")}
        </span>
      )}
    </SectionCard>
  );
}
